import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Input, 
  Spin, 
  Select, 
  Space, 
  Tag,
  Empty,
  Typography,
  ConfigProvider,
  Table,
  Modal,
  InputNumber,
  Form,
  Divider,
  List,
  Card,
  Row,
  Col
} from 'antd';
import { 
  FaSearch, 
  FaPlus,
  FaBoxOpen,
  FaSave,
  FaTrash
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';
import formatarValor from '../formatarValor';

const { Title } = Typography;

const Estoque = () => {
  // Estados principais
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Estados do modal de adicionar estoque
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchModalText, setSearchModalText] = useState('');
  const [quantidadesAdicionadas, setQuantidadesAdicionadas] = useState({});
  const [quantidadesSalvas, setQuantidadesSalvas] = useState({});
  const [salvandoProduto, setSalvandoProduto] = useState(null);
  
  // Novo estado para produtos adicionados
  const [produtosAdicionados, setProdutosAdicionados] = useState([]);

  // Cores do degradê
  const primaryColor = 'rgb(249, 130, 68)';
  const secondaryColor = 'rgb(247, 72, 115)';
  const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  // Buscar dados iniciais
  useEffect(() => {
    fetchData();
  }, []);

    useEffect(() => {
     document.title = "Estoque";
   
  }, []);

  // Buscar produtos adicionados quando o modal abrir
  useEffect(() => {
    if (isModalVisible) {
      fetchProdutosAdicionados();
    }
  }, [isModalVisible]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [produtosData, categoriasData] = await Promise.all([
        Api.ListProduto(),
        Api.ListCategoria()
      ]);
      
      // Formatar produtos
      const produtosFormatados = produtosData.map(p => ({ 
        id: p.id,
        descricao: p.descricao,
        preco: parseFloat(p.preco) || 0,
        status: p.status,
        quantidade: parseInt(p.qtd) || 0,
        id_categoria: p.id_categoria,
        key: p.id
      }));
      
      setProdutos(produtosFormatados);
      setCategorias(categoriasData);
    } catch (error) {
      toast.error('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Buscar produtos adicionados
  const fetchProdutosAdicionados = async () => {
    try {

      const dados={cod:'ENT'};
      const estoqueData = await Api.ListEstoque(dados);
      
      // Formatar os dados conforme a estrutura esperada
      const produtosAdicionadosFormatados = estoqueData.map(item => ({
        id: item.id,
        produto: item.produto,
        quantidade: parseInt(item.qtd) || 0,
        key: item.id
      }));
      
      setProdutosAdicionados(produtosAdicionadosFormatados);
    } catch (error) {
      toast.error('Erro ao carregar produtos adicionados: ' + error.message);
    }
  };

  // Remover produto adicionado
const removerProdutoAdicionado = async (id) => {
  try {

    const dados={id:id};
    await Api.DeleteEstoque(dados); // supondo que exista este endpoint
    toast.success('Produto removido com sucesso!');
    fetchProdutosAdicionados();
  } catch (error) {
    toast.error('Erro ao remover produto: ' + error.message);
  }
};

  // Filtrar produtos
  const filteredProdutos = produtos.filter(produto => {
    const matchesSearch = produto.descricao.toLowerCase().includes(searchText.toLowerCase());
    const matchesCategory = selectedCategory ? produto.id_categoria === selectedCategory : true;
    const matchesStatus = selectedStatus ? produto.status === selectedStatus : true;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Filtrar produtos no modal
  const filteredModalProdutos = produtos.filter(produto => 
    produto.descricao.toLowerCase().includes(searchModalText.toLowerCase())
  );

  // Abrir modal
  const abrirModalEstoque = () => {
    setQuantidadesAdicionadas({});
    setQuantidadesSalvas({});
    setSearchModalText('');
    setIsModalVisible(true);
  };

  // Atualizar quantidade adicionada de um produto
  const handleQuantidadeChange = (produtoId, quantidade) => {
    setQuantidadesAdicionadas(prev => ({
      ...prev,
      [produtoId]: quantidade || 0
    }));
  };

  // Salvar quantidade individual do produto
  const salvarQuantidadeProduto = async (produtoId) => {
    const quantidade = quantidadesAdicionadas[produtoId];
    
    if (!quantidade || quantidade <= 0) {
      toast.warning('Informe uma quantidade válida!');
      return;
    }

    try {
      setSalvandoProduto(produtoId);
      
      const formData = new FormData();
      formData.append('id_produto', produtoId);
      formData.append('qtd', quantidade.toString());
      formData.append('cod', 'ENT');

      const response = await Api.AddEstoque(formData);
      
      if (response.success) {
        toast.success('Quantidade salva com sucesso!');
        
        // Marcar como salvo
        setQuantidadesSalvas(prev => ({
          ...prev,
          [produtoId]: quantidade
        }));
        
        // Limpar quantidade digitada
        setQuantidadesAdicionadas(prev => ({
          ...prev,
          [produtoId]: 0
        }));

        // Atualizar lista de produtos adicionados
        await fetchProdutosAdicionados();
      } else {
        toast.error('Erro ao salvar quantidade');
      }
    } catch (error) {
      toast.error('Erro ao salvar: ' + error.message);
    } finally {
      setSalvandoProduto(null);
    }
  };

  // Finalizar adição de estoque
const finalizarAdicaoEstoque = async () => {
  try {
    setModalLoading(true);

    const response = await Api.AlterEstoque();
    // se for um axios response, garante que estamos lendo os dados
    const data = response?.data || response;

    if (!data) {
      throw new Error("Nenhuma resposta do servidor.");
    }

    if (data.success) {
      toast.success( "Estoque atualizado com sucesso!");

      // Atualiza a lista para refletir as mudanças
      await fetchData();

      // Fechar modal e limpar estados
      setIsModalVisible(false);
      setQuantidadesAdicionadas({});
      setQuantidadesSalvas({});
    } else {
      toast.error( "Erro ao atualizar estoque!");
    }
  } catch (error) {
    console.error("Erro ao finalizar:", error);
    toast.error("Erro ao finalizar: " + ( "Erro desconhecido."));
  } finally {
    setModalLoading(false);
  }
};

 

  // Colunas da tabela do modal (lado esquerdo)
  const columnsModal = [
    {
      title: 'Produto',
      dataIndex: 'descricao',
      key: 'descricao',
      width: '40%',
    },
    {
      title: 'Estoque Atual',
      dataIndex: 'quantidade',
      key: 'quantidade',
      width: 120,
      render: (quantidade) => (
        <Tag 
          color={quantidade > 10 ? 'green' : quantidade > 0 ? 'orange' : 'red'}
          style={{ 
            fontWeight: 'bold',
            fontSize: '12px',
            minWidth: '45px',
            textAlign: 'center'
          }}
        >
          {parseInt(quantidade) || 0}
        </Tag>
      ),
    },
    {
      title: 'Quantidade a Adicionar',
      key: 'adicionar',
      width: 280,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <InputNumber
            min={0}
            max={100000}
            value={quantidadesAdicionadas[record.id] }
            onChange={(value) => handleQuantidadeChange(record.id, value)}
            style={{ flex: 1, borderRadius: 6 }}
            placeholder="0"
            disabled={salvandoProduto === record.id}
          />
          <Button
            type="primary"
            icon={<FaPlus />}
            size="small"
            loading={salvandoProduto === record.id}
            onClick={() => salvarQuantidadeProduto(record.id)}
            disabled={!quantidadesAdicionadas[record.id] || quantidadesAdicionadas[record.id] <= 0}
            style={{
              background: gradient,
              border: 'none',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 32,
              height: 32
            }}
            title="Salvar quantidade"
          />
        </div>
      ),
    },
    {
      title: 'Qtd. Salva',
      key: 'salva',
      width: 100,
      render: (_, record) => {
        const qtdSalva = quantidadesSalvas[record.id];
        return qtdSalva ? (
          <Tag 
            color="green"
            style={{ 
              fontWeight: 'bold',
              fontSize: '12px',
              minWidth: '45px',
              textAlign: 'center'
            }}
          >
            +{qtdSalva}
          </Tag>
        ) : (
          <span style={{ color: 'rgba(0,0,0,0.3)' }}>-</span>
        );
      },
    },
  ];

  // Colunas da tabela principal
  const columns = [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      sorter: (a, b) => a.descricao.localeCompare(b.descricao),
      width: '35%',
    },
    {
      title: 'Preço',
      dataIndex: 'preco',
      key: 'preco',
      width: 120,
      render: (preco) => formatarValor(parseFloat(preco) || 0),
      sorter: (a, b) => parseFloat(a.preco) - parseFloat(b.preco),
    },
    {
      title: 'Estoque Atual',
      dataIndex: 'quantidade',
      key: 'quantidade',
      width: 140,
      render: (quantidade) => (
        <Tag 
          color={quantidade > 10 ? 'green' : quantidade > 0 ? 'orange' : 'red'}
          style={{ 
            fontWeight: 'bold',
            fontSize: '14px',
            minWidth: '50px',
            textAlign: 'center'
          }}
        >
          {parseInt(quantidade) || 0}
        </Tag>
      ),
      sorter: (a, b) => parseInt(a.quantidade) - parseInt(b.quantidade),
    },
    {
      title: 'Categoria',
      dataIndex: 'id_categoria',
      key: 'categoria',
      render: (id_categoria) => {
        const categoria = categorias.find(c => c.id === id_categoria);
        return categoria ? categoria.descricao : 'Sem categoria';
      },
      filters: categorias.map(c => ({ text: c.descricao, value: c.id })),
      onFilter: (value, record) => record.id_categoria === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag 
          color={status === 'DISPONÍVEL' ? 'green' : 'red'}
          style={{ 
            borderRadius: 4,
            fontWeight: 500,
          }}
        >
          {status === 'DISPONÍVEL' ? 'Disponível' : 'Indisponível'}
        </Tag>
      ),
      filters: [
        { text: 'Disponível', value: 'DISPONÍVEL' },
        { text: 'Indisponível', value: 'INDISPONÍVEL' },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
        },
      }}
    >
      <div style={{ 
        padding: 24,
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        <ToastContainer position="top-right" />
        
        {/* Cabeçalho */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: 24,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <Title level={2} style={{ 
            margin: 0,
            background: gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            ENTRADA EM ESTOQUE
          </Title>
          
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Input
              placeholder="Pesquisar produtos..."
              prefix={<FaSearch style={{ color: 'rgba(0,0,0,0.3)' }} />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: 250,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            />
            
            <Select
              placeholder="Filtrar por categoria"
              options={categorias.map(c => ({ value: c.id, label: c.descricao }))}
              value={selectedCategory}
              onChange={(value) => {
                setSelectedCategory(value);
                setCurrentPage(1);
              }}
              style={{ width: 180, borderRadius: 8 }}
              allowClear
            />
            
            <Select
              placeholder="Filtrar por status"
              options={[
                { value: 'DISPONÍVEL', label: 'Disponível' },
                { value: 'INDISPONÍVEL', label: 'Indisponível' }
              ]}
              value={selectedStatus}
              onChange={(value) => {
                setSelectedStatus(value);
                setCurrentPage(1);
              }}
              style={{ width: 150, borderRadius: 8 }}
              allowClear
            />

            <Button 
              type="primary"
              icon={<FaPlus />}
              onClick={abrirModalEstoque}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(247, 72, 115, 0.3)',
                height: 40,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              Adicionar Estoque
            </Button>
          </div>
        </div>

        {/* Informações de estoque */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            <div>
              <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px' }}>Total de Produtos:</span>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: primaryColor }}>
                {filteredProdutos.length}
              </div>
            </div>
            <div>
              <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px' }}>Produtos com Estoque:</span>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'green' }}>
                {filteredProdutos.filter(p => p.quantidade > 0).length}
              </div>
            </div>
            <div>
              <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: '14px' }}>Produtos sem Estoque:</span>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'red' }}>
                {filteredProdutos.filter(p => p.quantidade === 0).length}
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de Produtos */}
        <Spin spinning={loading}>
          {filteredProdutos.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredProdutos}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filteredProdutos.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} de ${total} produtos`,
                style: {
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }
              }}
              style={{
                background: '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
              scroll={{ x: true }}
              rowKey="id"
            />
          ) : (
            <Empty
              description={
                <span style={{ color: 'rgba(0,0,0,0.5)' }}>
                  Nenhum produto encontrado {searchText || selectedCategory || selectedStatus ? 'com os filtros atuais' : ''}
                </span>
              }
              style={{ 
                marginTop: 50,
                padding: 40,
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            />
          )}
        </Spin>

        {/* Modal de Adicionar Estoque - ATUALIZADO */}
        <Modal
          title={
            <Space style={{ fontSize: '18px', fontWeight: 600 }}>
              <FaBoxOpen style={{ color: primaryColor }} />
              <span style={{
                background: gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-block'
              }}>
                Adicionar Estoque
              </span>
            </Space>
          }
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          width={1200}
          footer={null}
          destroyOnClose
          styles={{
            header: {
              borderBottom: '1px solid rgba(0,0,0,0.05)',
              paddingBottom: 16
            }
          }}
        >
          <div style={{ marginTop: 16 }}>
            {/* Barra de pesquisa no modal */}
            <Input
              placeholder="Pesquisar produtos..."
              prefix={<FaSearch style={{ color: 'rgba(0,0,0,0.3)' }} />}
              value={searchModalText}
              onChange={(e) => setSearchModalText(e.target.value)}
              style={{ 
                marginBottom: 16,
                borderRadius: 8,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            />

            {/* Resumo */}
            <div style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: 'rgba(0,0,0,0.7)' }}>
                Total de produtos adicionados:
              </span>
              <Tag color={produtosAdicionados.length > 0 ? 'green' : 'default'} style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {produtosAdicionados.length}
              </Tag>
            </div>

            {/* Layout com dois painéis */}
            <Row gutter={[16, 16]}>
              {/* Painel esquerdo - Todos os produtos */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Todos os Produtos" 
                  size="small"
                  style={{ height: '400px' }}
                  styles={{ body: { padding: 0 } }}
                >
                  <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    <Table
                      scroll={{ x: true }}
                      columns={columnsModal}
                      dataSource={filteredModalProdutos}
                      pagination={false}
                      rowKey="id"
                      size="small"
                      locale={{
                        emptyText: 'Nenhum produto encontrado'
                      }}
                    />
                  </div>
                </Card>
              </Col>

              {/* Painel direito - Produtos adicionados */}
              <Col xs={24} lg={12}>
                <Card 
                  title="Produtos Adicionados" 
                  size="small"
                  style={{ height: '400px' }}
                  styles={{ body: { padding: 0 } }}
                >
                  <div style={{ maxHeight: '350px', overflowY: 'auto', padding: '8px' }}>
                    {produtosAdicionados.length > 0 ? (
                      <List
                        dataSource={produtosAdicionados}
                        renderItem={(item) => (
                          <List.Item
                            actions={[
                              <Button
                                type="text"
                                danger
                                icon={<FaTrash />}
                                size="small"
                                onClick={() => removerProdutoAdicionado(item.id)}
                                title="Remover produto"
                              />
                            ]}
                          >
                            <List.Item.Meta
                              title={item.produto}
                              description={
                                <Space>
                                  <span>Quantidade:</span>
                                  <Tag color="blue" style={{ fontWeight: 'bold' }}>
                                    {item.quantidade}
                                  </Tag>
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Empty
                        description="Nenhum produto adicionado"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ 
                          margin: '40px 0',
                          color: 'rgba(0,0,0,0.3)'
                        }}
                      />
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Botões de ação */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button
                onClick={() => setIsModalVisible(false)}
                style={{ borderRadius: 6 }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                icon={<FaSave />}
                loading={modalLoading}
                onClick={finalizarAdicaoEstoque}
                disabled={produtosAdicionados.length === 0}
                style={{ 
                  background: gradient,
                  border: 'none',
                  borderRadius: 6,
                  boxShadow: '0 2px 8px rgba(247, 72, 115, 0.3)'
                }}
              >
                Finalizar Adição de Estoque
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Estoque;